import { debounceIfPossible } from "@faire/web/common/globals/debounceIfPossible";
import { getWindow } from "@faire/web/common/globals/getWindow";
import { useRefFrom } from "@faire/web/ui/hooks/useRefFrom";
import { observer } from "mobx-react";
import React, {
  Suspense,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import styled from "styled-components";

import { IRoundQuantityPickerProps } from "@faire/retailer/components/RoundQuantityPicker";
import { IRoundQuantityPickerStateProps } from "@faire/retailer/components/RoundQuantityPicker/UseRoundQuantityPickerState";
import { TrackedLinkContext } from "@faire/retailer/components/TrackedLink";
import { useCartProductAddOptions } from "@faire/retailer/features/ProductAdd/useCartProductAddOptions";
import { LazyQuickAddModal } from "@faire/retailer/features/ProductQuickAddModal/LazyQuickAddModal";
import { useCartItems } from "@faire/retailer/hooks/cart/useCartItems";
import { useCartUpdate } from "@faire/retailer/hooks/cart/useCartUpdate";
import { useAppHistory } from "@faire/retailer/packages/core/services/AppHistory";
import { AddToCartVariants } from "@faire/retailer/stores/domain/CartStore/triggerAddToCartEvent";
import { ProductTileRoundQuantityOptionPicker } from "@faire/retailer/views/brand/carousels/ProductTileRoundQuantityOptionPicker";
import { BulkUpdateItemClass } from "@faire/retailer/views/brand/utils/bulkUpdateCartItem";

import { FaireProtectionTooltip } from "../BrandCartCard/components/FaireProtectionTooltip";

import { IEncapsulatedQuickAddProps } from "./LazyEncapsulatedQuickAdd";

const preventClickEvents = (e: React.MouseEvent<HTMLElement>) => {
  e.preventDefault();
  e.stopPropagation();
};

const EncapsulatedQuickAddBase: React.FC<
  IEncapsulatedQuickAddProps & React.HTMLAttributes<HTMLDivElement>
> = ({
  productToken,
  quickAdd,
  brandToken,
  imageContainerRef,
  updateItems,
  onQuickAddClick,
  onQuickAddSubmit,
  onChooseOption,
  onChooseOptionQuantityUpdate,
  showModalOnClick,
  renderCollapsableQuickAddButtonProps,
  disableQuickAddWithFaireProtectionTooltip,
  className,
  getCartItemTokenByProductToken,
  ...rest
}) => {
  const PRODUCT_TILE_PICKER_WIDTH = 32;
  const PRODUCT_TILE_PICKER_OFFSET = 8;
  const PRODUCT_TILE_PICKER_OFFSET_LARGE = 16;

  const [clientWidth, setClientWidth] = useState(PRODUCT_TILE_PICKER_WIDTH);

  const { getProductTileQuantityInCart } = useCartItems();
  const { updateCartItemsWithUpdateHandler } = useCartUpdate();
  const context = useContext(TrackedLinkContext);
  const { addQuery } = useAppHistory(["addQuery"]);

  useEffect(() => {
    const handleResize = debounceIfPossible(() => {
      setClientWidth(
        imageContainerRef?.current?.clientWidth ?? PRODUCT_TILE_PICKER_WIDTH
      );
    }, 200);

    getWindow()?.addEventListener("resize", handleResize);

    handleResize();

    return () => {
      getWindow()?.removeEventListener("resize", handleResize);
    };
  }, [imageContainerRef]);

  const [isEncapsulatedQuickAddModalOpen, setIsEncapsulatedQuickAddModalOpen] =
    useState(false);

  const cartQuantity = getProductTileQuantityInCart(productToken ?? "");

  const onChooseMultiOption = () => {
    const findByToken =
      getCartItemTokenByProductToken?.(productToken ?? "") ?? "";
    if (findByToken !== "") {
      addQuery({
        product: productToken,
        findByToken,
        updateQuantityOnChange: "true",
      });
    } else {
      setIsEncapsulatedQuickAddModalOpen(true);
    }
  };

  const productAddOptionsQuickAdd = useCartProductAddOptions(updateItems);

  const { current: bulkUpdateItemHelper } = useRefFrom(
    () =>
      new BulkUpdateItemClass(
        updateItems ?? updateCartItemsWithUpdateHandler,
        context?.containerName ?? "collection_page",
      )
  );

  const internalRenderCollapsableQuickAddButtonProps: IRoundQuantityPickerProps &
    IRoundQuantityPickerStateProps = {
    value: 0,
    stepSize: quickAdd?.quick_add_option?.option_unit_multiplier ?? 0,
    onChange: (value: number) => {
      onQuickAddClick?.(value);
      bulkUpdateItemHelper.bulkUpdateItems(
        productToken ?? "",
        quickAdd?.quick_add_option?.option_token ?? "",
        value,
        quickAdd?.quick_add_option === undefined,
        brandToken ?? "",
        AddToCartVariants.ENCAPSULATED_QUICK_ADD_BTN,
        () => onQuickAddSubmit?.(value)
      );
    },
    minValue: quickAdd?.quick_add_option?.option_min_order_quantity ?? 0,
    maxValue:
      quickAdd?.quick_add_option?.option_available_units ??
      Number.MAX_SAFE_INTEGER,
    isMultiOption: quickAdd?.quick_add_option === undefined,
    onChooseMultiOption,
    multiOptionText: quickAdd?.quick_add_text,
    showModalOnClick,
    ...renderCollapsableQuickAddButtonProps,
  };

  const onCloseModal = useCallback(() => {
    setIsEncapsulatedQuickAddModalOpen(false);
  }, []);

  const handleOnClick = () => {
    if (internalRenderCollapsableQuickAddButtonProps.isMultiOption) {
      if (internalRenderCollapsableQuickAddButtonProps.onChooseMultiOption) {
        internalRenderCollapsableQuickAddButtonProps.onChooseMultiOption();
      }
      onChooseOption && onChooseOption();
    }
  };

  const renderQuickAddModal = () => {
    if (productToken && isEncapsulatedQuickAddModalOpen) {
      return (
        <Suspense fallback={null}>
          <LazyQuickAddModal
            key={`${productToken}-lazy-quick-add-modal`}
            onClose={onCloseModal}
            onChooseOptionQuantityUpdate={onChooseOptionQuantityUpdate}
            productToken={productToken}
            productAddOptions={productAddOptionsQuickAdd}
            updateCartFn={updateItems}
          />
        </Suspense>
      );
    }

    return null;
  };

  const renderRoundQuantityPicker = () => {
    if (disableQuickAddWithFaireProtectionTooltip) {
      return (
        <FaireProtectionTooltip
          hidden={false}
          targetNode={
            <ProductTileRoundQuantityOptionPicker
              {...internalRenderCollapsableQuickAddButtonProps}
              value={cartQuantity}
              maxWidth={
                // either use container width or use the picker width
                clientWidth -
                (showModalOnClick
                  ? PRODUCT_TILE_PICKER_OFFSET_LARGE
                  : PRODUCT_TILE_PICKER_OFFSET)
              }
              disabled
            />
          }
          usePortal
          portalTooltipYOffset={REACT_PORTAL_TOOLTIP_Y_OFFSET}
        />
      );
    }

    return (
      <ProductTileRoundQuantityOptionPicker
        {...internalRenderCollapsableQuickAddButtonProps}
        value={cartQuantity}
        maxWidth={
          // either use container width or use the picker width
          clientWidth -
          (showModalOnClick
            ? PRODUCT_TILE_PICKER_OFFSET_LARGE
            : PRODUCT_TILE_PICKER_OFFSET)
        }
      />
    );
  };

  return (
    <div>
      {renderQuickAddModal()}
      <RoundQuantityPickerWrapper
        className={className}
        onClick={(e) => {
          preventClickEvents(e);
          handleOnClick();
        }}
        key={`${productToken}-round-quantity-picker`}
        $isMultiOption={
          !!internalRenderCollapsableQuickAddButtonProps.isMultiOption
        }
        data-test-id="quick-add-marketplace-product"
        $showModalOnClick={showModalOnClick}
        {...rest}
      >
        {renderRoundQuantityPicker()}
      </RoundQuantityPickerWrapper>
    </div>
  );
};

export const EncapsulatedQuickAdd = observer(EncapsulatedQuickAddBase);

export const RoundQuantityPickerWrapper = styled.div<{
  $isMultiOption?: boolean;
  $showModalOnClick?: boolean;
}>`
  position: absolute;
  bottom: 8px;
  right: 8px;
  z-index: 5;
`;

const REACT_PORTAL_TOOLTIP_Y_OFFSET = -4;
